/**
 * useRetreatEvents — Owns bell firing, wake-up container, auto-start, auto-complete,
 * and container-level session recording.
 */
import { useEffect, useRef, type Dispatch } from "react";
import type { EngineSnapshot } from "@retreats/engine";
import type { EditableRetreatSchedule, ContainerRecord, MeditationContainer } from "@retreat/core";
import type { UIState, UIAction } from "../bridge/types";
import type { LiveScreenData } from "./useLiveScreenData";
import {
  startSession,
  appendContainer,
  endSession,
  getActiveSessionId,
} from "../state/sessionRepository";
import { enqueueContainerSync, enqueueSessionSync, enqueueIntentionSync } from "../services/sessionSyncService";
import { syncManager } from "../services/SyncManager";
import { createChildLogger } from "@retreat/shared";
import { UI_ACTION_TYPES, SESSION_STATUS } from "../constants";

const log = createChildLogger("useRetreatEvents");

function buildContainerRecord(
  container: { id: string; name: string; type: string; durationSeconds: number },
  lastRunningElapsed: number,
  containerStartElapsed: number,
  isComplete: boolean,
): ContainerRecord {
  return {
    containerId: container.id,
    containerName: container.name,
    containerType: container.type,
    containerPosture: container.type === 'meditation' ? (container as MeditationContainer).posture : undefined,
    scheduledDurationSeconds: container.durationSeconds,
    actualDurationSeconds: Math.max(0, lastRunningElapsed - containerStartElapsed),
    completedAt: Date.now(),
    isComplete,
  };
}

function _recordPartialContainerAndClose(
  schedule: EditableRetreatSchedule,
  liveData: LiveScreenData,
  userId: string | null,
  lastRunningElapsed: number,
  containerStartElapsed: number,
): void {
  const allContainers = schedule.days.flatMap((d) => d.containers);
  const partialContainer = allContainers[liveData.containerIdx];
  log.info('Session stopped by user', { containerIdx: liveData.containerIdx, elapsedSeconds: lastRunningElapsed });
  if (!partialContainer) {
    log.warn('Stopped: no container at idx', { idx: liveData.containerIdx });
  } else {
    const record = buildContainerRecord(partialContainer, lastRunningElapsed, containerStartElapsed, false);
    const sessionId = getActiveSessionId();
    if (sessionId) {
      appendContainer(sessionId, record);
      if (userId) enqueueContainerSync(sessionId, record, userId);
      else log.info('Stopped without auth — container saved locally, will sync on login', { containerName: partialContainer.name });
      const header = endSession(sessionId, SESSION_STATUS.INCOMPLETE);
      if (header && userId) { enqueueSessionSync(header, userId); void syncManager.drain(); }
      else if (header) log.info('Session ended without auth — will sync on login', { sessionId });
    } else {
      log.warn('Stopped: no active session ID — partial container not saved');
    }
  }
}

function _recordLastContainerAndClose(
  schedule: EditableRetreatSchedule,
  liveData: LiveScreenData,
  userId: string | null,
  lastRunningElapsed: number,
  containerStartElapsed: number,
): void {
  log.info('Session completed (full schedule)', { elapsedSeconds: lastRunningElapsed });
  const sessionId = getActiveSessionId();
  if (!sessionId) {
    log.warn('Completed: no active session ID — session not recorded');
    return;
  }
  const allContainers = schedule.days.flatMap((d) => d.containers);
  const lastContainer = allContainers[liveData.containerIdx];
  if (lastContainer) {
    const record = buildContainerRecord(lastContainer, lastRunningElapsed, containerStartElapsed, true);
    log.debug('Recording last container before completion', { containerName: lastContainer.name, actualDuration: lastRunningElapsed - containerStartElapsed });
    appendContainer(sessionId, record);
    if (userId) enqueueContainerSync(sessionId, record, userId);
    else log.info('Completed without auth — last container saved locally, will sync on login', { containerName: lastContainer.name });
  } else {
    log.warn('Completed: no last container at idx', { idx: liveData.containerIdx });
  }
  const header = endSession(sessionId, SESSION_STATUS.COMPLETED);
  if (header && userId) { enqueueSessionSync(header, userId); void syncManager.drain(); }
  else if (header) log.info('Session completed without auth — will sync on login', { sessionId });
}

export function useRetreatEvents(
  snapshot: EngineSnapshot,
  uiState: UIState,
  liveData: LiveScreenData,
  dispatchUI: Dispatch<UIAction>,
  handlePreparationContinue: () => Promise<void>,
  schedule: EditableRetreatSchedule | null,
  userId: string | null,
): void {
  // Always-current refs so subscribe callbacks see live values without re-registering
  const liveDataRef = useRef(liveData);
  const scheduleRef = useRef(schedule);
  const userIdRef = useRef(userId);
  const intentionRecordRef = useRef(uiState.intentionRecord);
  useEffect(() => { liveDataRef.current = liveData; });
  useEffect(() => { scheduleRef.current = schedule; });
  useEffect(() => { userIdRef.current = userId; });
  useEffect(() => { intentionRecordRef.current = uiState.intentionRecord; });

  // Track the last elapsed value seen while RUNNING. The XState CLEANUP_DONE
  // transition resets elapsedSeconds to 0 before IDLE subscribers fire, so
  // snap.elapsedSeconds inside the subscriber is always 0 — this ref captures
  // the real elapsed from the React render cycle instead.
  const lastRunningElapsedRef = useRef<number>(0);
  useEffect(() => {
    if (snapshot.status === 'RUNNING') {
      lastRunningElapsedRef.current = snapshot.elapsedSeconds;
    }
  });

  // Wake-up container — dismiss when first bell fires
  const prevWakeUpBellRef = useRef<number | null>(null);
  useEffect(() => {
    if (!uiState.showWakeUpContainer) return;
    if (
      snapshot.lastBellFiredAt !== null &&
      snapshot.lastBellFiredAt !== prevWakeUpBellRef.current
    ) {
      dispatchUI({ type: UI_ACTION_TYPES.DISMISS_WAKE_UP_CONTAINER });
    }
    prevWakeUpBellRef.current = snapshot.lastBellFiredAt;
  }, [uiState.showWakeUpContainer, snapshot.lastBellFiredAt, dispatchUI]);

  // Transition bell → container recording.
  // Seed with the snapshot value so a recovered engine (non-null lastTransitionBellFiredAt
  // from a prior run) does not fire the effect spuriously on mount — which would incorrectly
  // advance containerStartElapsedRef and truncate the final container's duration.
  const prevTransitionBellRef = useRef<number | null>(snapshot.lastTransitionBellFiredAt);
  const containerStartElapsedRef = useRef<number>(0);

  useEffect(() => {
    const { lastTransitionBellFiredAt } = snapshot;
    if (
      lastTransitionBellFiredAt !== null &&
      lastTransitionBellFiredAt !== prevTransitionBellRef.current
    ) {
      // Record the container that just completed. When the transition bell fires,
      // liveData.dayProgress.idx has already advanced to the next container — subtract
      // 1 to get the container that just finished.
      const currentSchedule = scheduleRef.current;
      if (currentSchedule) {
        // snapshot.elapsedSeconds can be 0 when the engine batches the completion
        // bell and CLEANUP_DONE reset into the same React render. Fall back to the
        // last captured RUNNING elapsed — same pattern as _recordLastContainerAndClose.
        const elapsed = snapshot.elapsedSeconds > 0
          ? snapshot.elapsedSeconds
          : lastRunningElapsedRef.current;

        const allContainers = currentSchedule.days.flatMap((d) => d.containers);
        const completedContainer = allContainers[liveData.dayProgress.idx - 1];
        if (completedContainer) {
          const actualDuration = elapsed - containerStartElapsedRef.current;
          const record: ContainerRecord = {
            containerId: completedContainer.id,
            containerName: completedContainer.name,
            containerType: completedContainer.type,
            containerPosture:
              completedContainer.type === "meditation"
                ? (completedContainer as MeditationContainer).posture
                : undefined,
            scheduledDurationSeconds: completedContainer.durationSeconds,
            actualDurationSeconds: Math.max(0, actualDuration),
            completedAt: Date.now(),
            isComplete: true,
          };
          log.debug("Transition bell: recording completed container", {
            containerName: completedContainer.name,
            idx: liveData.dayProgress.idx - 1,
            actualDuration,
            scheduledDuration: completedContainer.durationSeconds,
          });
          const sessionId = getActiveSessionId();
          if (sessionId) {
            appendContainer(sessionId, record);
            const currentUserId = userIdRef.current;
            if (currentUserId) {
              enqueueContainerSync(sessionId, record, currentUserId);
            } else {
              log.debug(
                "Transition bell: container saved locally, no auth — will sync on login",
                { containerName: completedContainer.name },
              );
            }
          } else {
            log.warn(
              "Transition bell: no active session ID — container not recorded",
              { containerName: completedContainer.name },
            );
          }
        } else {
          log.warn("Transition bell: no container at idx", {
            idx: liveData.dayProgress.idx - 1,
          });
        }
        // Update ref for next container AFTER recording current one (use same
        // elapsed to stay consistent with the duration we just recorded).
        containerStartElapsedRef.current = elapsed;
      }

      prevTransitionBellRef.current = lastTransitionBellFiredAt;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    snapshot.lastTransitionBellFiredAt,
    liveData.dayProgress.idx,
    snapshot.elapsedSeconds,
    dispatchUI,
  ]);

  // Auto-start when preparation was already seen
  useEffect(() => {
    if (
      snapshot.status === "INITIALIZING" &&
      uiState.preparationSeen &&
      !uiState.isBooting
    ) {
      handlePreparationContinue();
    }
  }, [
    snapshot.status,
    uiState.preparationSeen,
    uiState.isBooting,
    handlePreparationContinue,
  ]);

  // Start a new session record when engine first goes RUNNING.
  // Guard against page reload mid-retreat (existing active session ID means we resumed).
  const sessionStartedRef = useRef(false);
  useEffect(() => {
    if (snapshot.status === 'RUNNING' && !sessionStartedRef.current) {
      sessionStartedRef.current = true;
      const existingId = getActiveSessionId();
      const currentSchedule = scheduleRef.current;
      if (!existingId && currentSchedule) {
        const allContainers = currentSchedule.days.flatMap((d) => d.containers);
        const totalScheduledSeconds = allContainers.reduce((s, c) => s + c.durationSeconds, 0);
        const newSession = startSession(currentSchedule.id ?? 'unknown', currentSchedule.name ?? 'retreat', totalScheduledSeconds);
        containerStartElapsedRef.current = 0;
        const currentUserId = userIdRef.current;
        const currentIntention = intentionRecordRef.current;
        if (currentUserId) {
          enqueueSessionSync(newSession, currentUserId);
          enqueueIntentionSync(newSession.id, currentIntention, currentUserId);
          void syncManager.drain();
        } else {
          log.info('New session started without auth — will sync on login', { sessionId: newSession.id });
        }
        log.info('New session started', { sessionId: newSession.id, retreatName: newSession.retreatName });
      } else if (existingId) {
        containerStartElapsedRef.current = snapshot.elapsedSeconds;
        log.info('Resumed existing session', { sessionId: existingId, elapsedSeconds: snapshot.elapsedSeconds });
      }
    }
    if (snapshot.status === 'IDLE') {
      sessionStartedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.status]);

  // RUNNING → IDLE: record partial/last container and guard SHOW_COMPLETION.
  // Driven by snapshot-diffing (no direct manager.subscribe).
  // completionType is observable on the snapshot until the next initialize() cycle
  // (cleared when the machine enters 'initializing'), so it is safe to read here.
  const showCompletionRef = useRef(uiState.showCompletion);
  const showEndConfirmRef = useRef(uiState.showEndConfirm);
  useEffect(() => {
    showCompletionRef.current = uiState.showCompletion;
    showEndConfirmRef.current = uiState.showEndConfirm;
  });

  const prevStatusRef = useRef(snapshot.status);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const status = snapshot.status;
    prevStatusRef.current = status;

    const isSessionEndTransition = prevStatus === "RUNNING" && status === "IDLE";
    if (!isSessionEndTransition) return;

    const currentSchedule = scheduleRef.current;
    const currentLiveData = liveDataRef.current;
    const currentUserId = userIdRef.current;
    const completionType = snapshot.completionType;

    if (completionType === "stopped" && currentSchedule) {
      _recordPartialContainerAndClose(
        currentSchedule,
        currentLiveData,
        currentUserId,
        lastRunningElapsedRef.current,
        containerStartElapsedRef.current,
      );
    }

    if (
      completionType === "completed" &&
      !showCompletionRef.current &&
      !showEndConfirmRef.current
    ) {
      if (currentSchedule) {
        _recordLastContainerAndClose(
          currentSchedule,
          currentLiveData,
          currentUserId,
          lastRunningElapsedRef.current,
          containerStartElapsedRef.current,
        );
      } else {
        log.warn('Completed: no schedule available — session not recorded');
      }
      dispatchUI({ type: UI_ACTION_TYPES.SHOW_COMPLETION });
    }
  }, [snapshot.status, snapshot.completionType, dispatchUI]);
}
